import "./ModalExternalTransactions.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Form, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";

import AppButton from "../../commons/buttons/AppButton";
import AppContainer from "../../commons/containers/AppContainer";
import AppCurrencyInputV2 from "../../commons/inputs/AppCurrencyInputV2";
import AppInputV2 from "../../commons/inputs/AppInputV2";
import AppLinkButton from "../../commons/buttons/AppLinkButton";
import AppLoading from "../../commons/AppLoading";
import AppTextAreaV2 from "../../commons/inputs/AppTextAreaV2";
import CalendarInputV2 from "../../commons/inputs/CalendarInputV2";
import CompanyAPI from "../company/CompanyAPI";
import ExternalTransactionAPI from "./services/ExternalTransactionAPI";
import HallAppToast from "../../commons/HallAppToast";
import { formatDateInEN } from "../../commons/util/HallAppUtil";

const companyAPI = new CompanyAPI();
const externalTransactionAPI = new ExternalTransactionAPI();

function ModalExternalTransactions({
  show,
  closeShow,
  transactionToEdit,
  callTransactions,
}) {
  const { Formik } = formik;

  const [initialValues, setInitialValues] = useState({
    name: "",
    description: "",
    type: "INPUT",
    recurrenceType: "NONE",
    installmentsTotal: "",
  });

  const [isLoading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [transactionDate, setTransactionDate] = useState(new Date());
  const [value, setValue] = useState(0);
  const [isEdit, setIsEdit] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().required("Nome é obrigatório").max(50),
    type: yup.string().required("Tipo é obrigatório"),
    recurrenceType: yup.string().required(),
    installmentsTotal: yup
      .number()
      .when("recurrenceType", {
        is: "INSTALLMENTS",
        then: (s) =>
          s
            .required("Informe o número de parcelas")
            .min(2, "Mínimo 2 parcelas")
            .max(120, "Máximo 120 parcelas"),
        otherwise: (s) => s.nullable(),
      }),
  });

  const createOrUpdate = async (values, { resetForm }) => {
    try {
      setLoading(true);

      const payload = {
        ...values,
        value,
        transactionDate: transactionDate || formatDateInEN(new Date()),
        companyId: companyAPI.getCompanyManagment().id,
      };

      if (isEdit) {
        await externalTransactionAPI.update(transactionToEdit.externalTransactionId, payload);
      } else {
        await externalTransactionAPI.create(payload);
      }

      HallAppToast.success();
      resetForm();
      setValue(0);

      callTransactions?.();
      closeShow();
    } catch {
      HallAppToast.genericError();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (transactionToEdit) {
      setIsEdit(true);

      const [y, m, d] = transactionToEdit.transactionDate.split("-");
      const date = new Date(y, m - 1, d);

      setStartDate(date);
      setTransactionDate(formatDateInEN(date));
      setValue(transactionToEdit.value);

      setInitialValues({
        name: transactionToEdit.name,
        description: transactionToEdit.description,
        type: transactionToEdit.type,
        recurrenceType: transactionToEdit.recurrenceType ?? "NONE",
        installmentsTotal: transactionToEdit.installmentsTotal ?? "",
      });
    }
  }, [transactionToEdit]);

  return (
    <Modal show={show} fullscreen onHide={closeShow}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>
          {isEdit ? "ATUALIZAR" : "ADICIONAR"} MOVIMENTAÇÃO
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <AppContainer>
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={createOrUpdate}
            enableReinitialize
          >
            {({ handleSubmit, handleChange, values, errors, touched }) => (
              <Form noValidate onSubmit={handleSubmit}>
                {/* 🔁 RECORRÊNCIA */}
                <div className="mb-2 section-title">
                  Essa movimentação se repete?
                </div>

                <div className="section-subtitle mb-3">
                  Defina se é única, fixa mensal ou parcelada.
                </div>

                <div className="row g-2 mb-3">
                  {[
                    {
                      key: "NONE",
                      label: "Única",
                      hint: "Acontece apenas uma vez",
                    },
                    {
                      key: "FIXED",
                      label: "Fixo mensal",
                      hint: "Repete todo mês sem fim",
                    },
                    {
                      key: "INSTALLMENTS",
                      label: "Parcelado",
                      hint: "Tem número definido de meses",
                    },
                  ].map((opt) => {
                    const active = values.recurrenceType === opt.key;

                    return (
                      <div className="col-4" key={opt.key}>
                        <button
                          type="button"
                          className="recurrence-card w-100"
                          data-active={active}
                          onClick={() =>
                            handleChange({
                              target: {
                                name: "recurrenceType",
                                value: opt.key,
                              },
                            })
                          }
                        >
                          <div className="recurrence-card__title">
                            {opt.label}
                          </div>
                          <div className="recurrence-card__hint">
                            {opt.hint}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {values.recurrenceType === "INSTALLMENTS" && (
                  <AppInputV2
                    label="Número de parcelas"
                    name="installmentsTotal"
                    type="number"
                    isRequired
                    value={values.installmentsTotal}
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                  />
                )}

                {/* 🟣 TIPO */}
                <div className="mb-2 section-title">
                  Tipo de movimentação
                </div>

                <div className="row g-2 mb-3">
                  {[
                    { key: "INPUT", label: "Entrada" },
                    { key: "OUTPUT", label: "Saída" },
                  ].map((opt) => {
                    const active = values.type === opt.key;

                    return (
                      <div className="col-6" key={opt.key}>
                        <button
                          type="button"
                          className="type-card w-100"
                          data-active={active}
                          onClick={() =>
                            handleChange({
                              target: { name: "type", value: opt.key },
                            })
                          }
                        >
                          {opt.label}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <AppCurrencyInputV2
                  label="Valor"
                  isRequired
                  value={value}
                  onChange={(_, v) => setValue(v)}
                />

                <CalendarInputV2
                  selectionMode="single"
                  setDates={setStartDate}
                  changeDates={setTransactionDate}
                  values={startDate}
                />

                <AppInputV2
                  label="Nome"
                  name="name"
                  isRequired
                  value={values.name}
                  onChange={handleChange}
                  errors={errors}
                  touched={touched}
                />

                <AppTextAreaV2
                  label="Descrição"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                />

                {isLoading ? (
                  <AppLoading />
                ) : (
                  <AppButton
                    width="100%"
                    type="submit"
                    name={isEdit ? "atualizar" : "salvar"}
                  />
                )}

                <div style={{ marginTop: 10 }}>
                  <AppLinkButton name="fechar" onClick={closeShow} />
                </div>
              </Form>
            )}
          </Formik>
        </AppContainer>
      </Modal.Body>
    </Modal>
  );
}

export default ModalExternalTransactions;
